import { Box, Theme, Typography, Alert } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTextField from "../../components/CustomTextField";
import CustomButton from "../../components/CustomButton";
import { UserManager } from "../../services/api/UserManager";
import { Routes } from "../../navigation/navigation-types";
import { getErrorMessage } from "../../utils/error-utils";

const MARGIN_HIGHER = 20;

const PasswordResetConfirm: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError(t("resetPassword.invalidLink") || "Invalid link");
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, t]);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setValidationError(
        t("resetPassword.passwordTooShort") || "Password too short",
      );
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleNewPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setNewPassword(value);
    if (value.length > 0) {
      validatePassword(value);
    } else {
      setValidationError("");
    }
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmPassword(event.target.value);
  };

  const handleSubmit = async () => {
    // Validate password
    if (!validatePassword(newPassword)) {
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setValidationError(
        t("resetPassword.passwordsMismatch") || "Passwords do not match",
      );
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      const userManager = UserManager.getManager();
      await userManager.confirmPasswordResetAsync(token, newPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(Routes.login);
      }, 3000);
    } catch (e: any) {
      setIsLoading(false);
      setError(getErrorMessage(e));
      return; // Don't continue to success state
    }
  };

  const isFormValid =
    newPassword.length >= 8 &&
    confirmPassword.length >= 8 &&
    newPassword === confirmPassword &&
    !validationError;

  // Error state - show error message but allow retry
  if (error && !success && !token) {
    // If no token, show error and back to login
    return (
      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.content}>
            <Typography className={classes.title}>
              {t("resetPassword.errorTitle")}
            </Typography>
            <Alert
              severity='error'
              style={{ marginTop: MARGIN_HIGHER, marginBottom: MARGIN_HIGHER }}
            >
              {error}
            </Alert>
            <CustomButton
              title={t("resetPassword.backToLogin")}
              size='medium'
              onClick={() => navigate(Routes.login)}
              style={{ width: "100%", marginBottom: 16 }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.content}>
            <Typography className={classes.title}>
              {t("resetPassword.success")}
            </Typography>
            <Alert
              severity='success'
              style={{ marginTop: MARGIN_HIGHER, marginBottom: MARGIN_HIGHER }}
            >
              {t("resetPassword.successMessage")}
            </Alert>
            <Typography
              variant='body2'
              color='text.secondary'
              style={{ marginTop: MARGIN_HIGHER }}
            >
              {t("resetPassword.redirecting") || "Redirecting"}...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Form state
  return (
    <Box className={classes.container}>
      <Box className={classes.card}>
        <Box className={classes.content}>
          <Typography
            className={classes.title}
            sx={{ marginTop: "0 !important", marginBottom: "1rem !important" }}
          >
            {t("resetPassword.title")}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            style={{ marginBottom: 24 }}
          >
            {t("resetPassword.description")}
          </Typography>

          {error && (
            <Alert severity='error' style={{ marginBottom: MARGIN_HIGHER }}>
              {error}
            </Alert>
          )}

          <Box style={{ marginBottom: MARGIN_HIGHER }}>
            <CustomTextField
              label={t("resetPassword.newPassword") || "New Password"}
              type='password'
              fullWidth
              value={newPassword}
              onChange={handleNewPasswordChange}
              isRequired
            />
          </Box>

          <Box style={{ marginBottom: MARGIN_HIGHER }}>
            <CustomTextField
              label={t("resetPassword.confirmPassword") || "Confirm Password"}
              type='password'
              fullWidth
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              isRequired
            />
          </Box>

          {validationError && (
            <Alert severity='warning' style={{ marginBottom: MARGIN_HIGHER }}>
              {validationError}
            </Alert>
          )}

          <CustomButton
            title={t("resetPassword.submit")}
            size='medium'
            onClick={handleSubmit}
            isDisabled={!isFormValid}
            isLoading={isLoading}
            style={{ width: "100%", marginBottom: 16, marginTop: 24 }}
          />

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography
              className={classes.textUnderline}
              onClick={() => navigate(Routes.login)}
            >
              {t("resetPassword.backToLogin")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.main.primary.d2,
    overflow: "scroll",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: theme.palette.natural.t6,
    boxShadow: theme.palette.main.shadow.primary,
    borderRadius: 16,
  },
  content: {
    padding: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginTop: 40,
    color: theme.palette.main.secondary.u1,
    marginBottom: 45,
  },
  textUnderline: {
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
    cursor: "pointer",
    fontSize: 14,
  },
}));

export default PasswordResetConfirm;
